/*
 * Homepage Product Switcher
 *
 * Renders the PDP top section (main-product) inline on a page template, with
 * the product family selector inside it acting as the tab strip. Switching
 * tabs swaps the embedded section's HTML via Shopify's Section Rendering API
 * (?section_id=main-product) so all variant / subscription / add-to-cart
 * logic stays in real PDP-context Liquid.
 *
 * Wiring rationale:
 *  - We intentionally let the embedded PDP family selector render. Its DOM
 *    is what the customer clicks on. We just intercept clicks in the capture
 *    phase so PDP family-selector.js (which would try to swap
 *    #shopify-section-main, doesn't exist here) never sees them. It also
 *    early-exits when nested inside [data-homepage-product-switcher].
 *  - URL state is updated via replaceState (not pushState) — the user is on
 *    /pages/about-us, not a PDP, so we just append ?product=<handle> for
 *    shareable links without rewriting the path.
 *  - Pre-fetches the other family products on idle so subsequent tab switches
 *    are instant.
 *  - On add-to-cart success, the existing cart-drawer.js global listeners
 *    handle drawer open behavior — same flow as the PDP, no extra wiring.
 */
(function () {
  // Shared cache keyed by handle so multiple instances of the switcher (if any)
  // and repeat clicks all benefit.
  var cache = {};
  // URLs we've already kicked into the browser HTTP cache via <img>/<link>
  // preload — avoid re-issuing the same request on re-prefetch.
  var posterWarmCache = new Set();
  // Live DOM cache: rendered <section> nodes keyed by handle. Lets us swap
  // back to a previously-visited product without re-parsing HTML or letting
  // the browser re-decode video poster images. The cached node was already
  // attached + painted once; re-attaching it is essentially free, so the
  // video thumbnails stay visible across tab clicks instead of flashing
  // black (.video-review-item has `background: #000`, so even a 50–200ms
  // poster-decode delay on remount reads as a hard black blink). Only the
  // HTML-cache (string) doesn't help here — fresh <video> elements always
  // pay the decode/paint cost, even with HTTP-cached posters. We populate
  // domCache lazily on swap-AWAY: when the user clicks to leave a product,
  // we stash that product's live DOM. The initial product is stashed when
  // the user first navigates away from it.
  var domCache = {};

  // Intelligems price test (TASKS #133). On full page loads Intelligems sends
  // a shopper in a test group to its copy of each product in the test (see
  // "Copies" in intelligems-bridge.js). This switcher fetches products itself,
  // so it asks for the copy the same way: products[] and the tabs keep the
  // original handles, the fetch uses the copy's handle and variant, and both
  // caches key on the handle actually fetched. product-family-selector.js does
  // the same for the PDP switcher.
  var IG_WAIT_MS = 2000;
  var IG_READY_EVENT = 'jiyu:intelligems-ready';

  // { handle, variantId, known }: what to fetch for a tab's product, as of now.
  function igProduct(handle, info) {
    var variantId = info ? info.variantId : null;
    var ig = window.JiyuIntelligems;
    if (!ig || typeof ig.productFor !== 'function') {
      return { handle: handle, variantId: variantId || null, known: true };
    }
    return ig.productFor(handle, variantId);
  }

  // Resolves once Intelligems is ready or the wait runs out.
  function igReady() {
    var ig = window.JiyuIntelligems;
    if (!ig || typeof ig.whenReady !== 'function') return Promise.resolve(true);
    return ig.whenReady(IG_WAIT_MS);
  }

  function productUrl(target, extraParam) {
    // Prefix with window.Shopify.routes.root so Spanish (/es/) and any
    // future locale markets fetch the translated PDP instead of always
    // hitting the default-locale English version. On default locale
    // routes.root is just "/", so the URL stays /products/<handle>.
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var params = [];
    if (target.variantId) params.push('variant=' + encodeURIComponent(target.variantId));
    if (extraParam) params.push(extraParam);
    return rootUrl + 'products/' + encodeURIComponent(target.handle) + (params.length ? '?' + params.join('&') : '');
  }

  // Walk a piece of cached product HTML, find every image referenced by the
  // video_reviews block (`<video data-poster>` and its overlay `<img>` for
  // video items, `<img src>` for poster-only items), and attach an `<img>`
  // for each into a hidden offscreen container.
  //
  // Why attach to DOM instead of just `new Image()`: bare `new Image()`
  // warms the HTTP cache but doesn't reliably pre-decode the bitmap, and
  // the bitmap cache is what makes paint instant. Browsers only keep
  // decoded bitmaps live for images that are reachable from the document
  // (or that have explicit references). When the customer switches to a
  // sibling product for the first time, the new section's <img
  // loading="eager" fetchpriority="high"> still has to decode the poster
  // (~50-200ms for a 360-wide WebP) before it can paint, even with the
  // bytes locally cached. Anchoring the warmed `<img>` in the document
  // forces the browser to fully decode the bitmap once and keep it
  // available for the actual section img to reuse.
  //
  // Don't use `<link rel="preload" as="image">` here. The image-preload
  // cache slot isn't reliably reused by `<video poster>` in Chromium,
  // and high-fetchpriority preload links can crowd out the section HTML
  // fetch on quick back-and-forth tab clicks.
  function getPosterWarmContainer() {
    var c = document.getElementById('hps-poster-warm-container');
    if (!c) {
      c = document.createElement('div');
      c.id = 'hps-poster-warm-container';
      c.setAttribute('aria-hidden', 'true');
      // Off the page (left:-9999px) but kept laid-out so the browser
      // actually decodes the images. visibility:hidden would make the
      // browser skip decode in some engines. contain:strict isolates
      // the layout/paint from the rest of the page.
      c.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;contain:strict;';
      document.body.appendChild(c);
    }
    return c;
  }

  function warmVideoPosterCache(html) {
    if (!html) return;
    var doc;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return;
    }
    var urls = new Set();
    doc.querySelectorAll('.video-review-item video[data-poster]').forEach(function (v) {
      var u = v.getAttribute('data-poster');
      if (u) urls.add(u);
    });
    // Fallback rendering path inside video_reviews: when a block is
    // configured with a poster but no video file, main-product.liquid
    // outputs an `<img src>` instead of a `<video poster>`.
    doc.querySelectorAll('.video-review-item img[src]').forEach(function (img) {
      var u = img.getAttribute('src');
      if (u) urls.add(u);
    });
    if (urls.size === 0) return;
    var container = getPosterWarmContainer();
    urls.forEach(function (u) {
      if (posterWarmCache.has(u)) return;
      posterWarmCache.add(u);
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.src = u;
      container.appendChild(img);
    });
  }

  // The video_reviews poster overlays render loading="lazy" so a page load
  // never fetches the hidden layout's copies (TASKS #143). An injected section
  // may be in view as it mounts, and iOS Safari holds a lazy image in swapped-
  // in DOM until after first paint, a black blink on the #000 item, so make
  // the shown layout's copies eager + high again before insertion. Twin of
  // _pfsEagerVideoPosters in product-family-selector.js.
  function eagerVideoPosters(root) {
    var layout = window.matchMedia('(min-width: 750px)').matches ? 'desktop' : 'mobile';
    root.querySelectorAll('.reviews-block-video-' + layout + ' img.video-review-eager-poster').forEach(function (img) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
    });
  }

  function init() {
    document.querySelectorAll('[data-homepage-product-switcher]').forEach(setupSwitcher);
  }

  function setupSwitcher(sectionEl) {
    if (sectionEl._hpsSetup) return;
    sectionEl._hpsSetup = true;

    var defaultHandle = sectionEl.dataset.defaultHandle;
    var preselectIdx = parseInt(sectionEl.dataset.preselectVariantIndex, 10) || 2;

    // Family lookup table from the section's inline JSON.
    var dataScript = sectionEl.querySelector('[data-hps-handles]');
    var products = {};
    if (dataScript) {
      try { products = JSON.parse(dataScript.textContent); } catch (e) {}
    }

    if (!products || Object.keys(products).length === 0) {
      // No related_products metafield set — surface a clear message instead of
      // silently spinning the skeleton forever.
      var contentBlank = sectionEl.querySelector('[data-hps-content]');
      if (contentBlank) {
        contentBlank.innerHTML = '<p class="hps__error">No related products configured. Set <code>custom.related_products</code> on the default product.</p>';
        contentBlank.removeAttribute('aria-busy');
      }
      return;
    }

    // Initial product = ?product= query param if it matches a family member,
    // otherwise the customizer default.
    var urlParams = new URLSearchParams(window.location.search);
    var initialHandle = urlParams.get('product');
    if (!initialHandle || !products[initialHandle]) initialHandle = defaultHandle;
    if (!products[initialHandle]) initialHandle = Object.keys(products)[0];

    loadProduct(sectionEl, initialHandle, products[initialHandle]);

    // If the URL arrives with #hps-buy, the browser has already scrolled
    // to the section anchor (top of switcher). Once the embedded PDP
    // finishes injecting, refine the scroll to land on the variant
    // picker / buy area instead of the section header. Listen once;
    // each loadProduct() dispatches jiyu:hps:product-changed when done.
    if (window.location.hash === '#hps-buy') {
      var onceLoaded = function () {
        document.removeEventListener('jiyu:hps:product-changed', onceLoaded);
        scrollToBuyBox(sectionEl);
      };
      document.addEventListener('jiyu:hps:product-changed', onceLoaded);
    }

    // Pre-cache the other family members on idle. Browsers throttle parallel
    // fetches automatically — three small requests is fine. After Intelligems
    // (capped), so a shopper in a test group warms the copies they will see.
    var schedule = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1500); };
    schedule(function () {
      igReady().then(function () {
        Object.keys(products).forEach(function (h) {
          if (h !== initialHandle) fetchProduct(igProduct(h, products[h]), products[h]);
        });
      });
    });

    // Intelligems became ready after a wait ran out, or a preview switched
    // test group: if the product on show isn't the one this shopper should
    // see, load it. Skipped while a load is waiting; that load decides itself.
    document.addEventListener(IG_READY_EVENT, function () {
      var shown = sectionEl._hpsShown;
      if (!shown || sectionEl._hpsWaiting || !products[shown.handle]) return;
      var target = igProduct(shown.handle, products[shown.handle]);
      if (target.known && target.handle !== shown.key) {
        loadProduct(sectionEl, shown.handle, products[shown.handle]);
      }
    });

    // Session-scoped memory of the last bundle the customer viewed via this
    // switcher. When they click the Bundles tab from a non-bundle product,
    // we restore them to this bundle instead of falling back to the first
    // entry in bundles_tab_products. Updated below on every successful
    // load via the jiyu:hps:product-changed event.
    var lastBundleHandle = null;
    document.addEventListener('jiyu:hps:product-changed', function () {
      // Active Bundles tab carries data-handle=<current product handle>
      // and only renders when the current product is in
      // bundles_tab_products (see Liquid in main-product.liquid).
      var activeBundleTab = sectionEl.querySelector('[data-pfs-bundles-tab].pfs__option--active');
      if (activeBundleTab) {
        var h = activeBundleTab.getAttribute('data-handle');
        if (h) lastBundleHandle = h;
      }
    });

    // Capture-phase click listener. The PDP family-selector buttons inside the
    // embedded HTML carry data-product-family-switch (or data-handle on the
    // current-product div). Either is a valid target.
    sectionEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-handle]');
      if (!btn || !sectionEl.contains(btn)) return;
      var handle = btn.dataset.handle;

      // Bundles-tab restore. If the customer is clicking the (inactive)
      // Bundles tab AND has previously viewed a bundle this session, swap
      // the click target to that remembered bundle. Without this, the tab
      // always navigates to the first bundle in bundles_tab_products,
      // erasing the customer's selection when they switch away from and
      // back to the Bundles tab.
      if (
        btn.hasAttribute('data-pfs-bundles-tab') &&
        !btn.classList.contains('pfs__option--active') &&
        lastBundleHandle &&
        lastBundleHandle !== handle &&
        products[lastBundleHandle]
      ) {
        handle = lastBundleHandle;
      }

      if (!handle || !products[handle]) return;
      e.preventDefault();
      e.stopPropagation();
      // Skip if already showing this product — avoids re-running inline
      // scripts and resetting transient state (subscription radio, qty, etc).
      var activeTab = sectionEl.querySelector('[data-hps-content] .pfs__option--active[data-handle]');
      var currentHandle = activeTab ? activeTab.getAttribute('data-handle') : null;
      if (currentHandle === handle) return;
      // The swap replaces the embedded PDP, this button included. After a
      // switch from the keyboard, focus follows to the new product's entry
      // instead of falling to <body>. The helpers live in
      // product-family-selector.js, which this section always loads.
      var focusApi = window.JiYuSwitcherFocus;
      var focusRequest = focusApi && focusApi.keyboardActivated(e, btn)
        ? { isCard: btn.classList.contains('pfs__bundle-card'), focusAtClick: document.activeElement }
        : null;
      // Stop the embedded PDP's review videos first: one still downloading
      // shares the connection with this fetch (TASKS #177).
      if (window.JiYuVideoReviews && window.JiYuVideoReviews.stopAll) window.JiYuVideoReviews.stopAll(sectionEl);
      loadProduct(sectionEl, handle, products[handle], focusRequest);
      updateUrlParam(handle);
    }, true);

    // Document-level handler for "Shop Now" buttons elsewhere on the page
    // (product spotlight, hero, etc) that link to ?product=<handle>#hps-buy.
    // Without this, the browser would update the URL on click but not
    // actually switch the embedded PDP (no full reload, no hashchange that
    // implies a product swap). Intercept same-page links matching one of
    // our family handles, switch the product, then trigger the scroll.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      var u;
      try { u = new URL(href, window.location.href); } catch (_) { return; }
      if (u.origin !== window.location.origin) return;
      if (u.pathname !== window.location.pathname) return;
      var nextHandle = u.searchParams.get('product');
      if (!nextHandle || !products[nextHandle]) return;

      e.preventDefault();
      var wantBuyScroll = u.hash === '#hps-buy';

      // Push the new URL state so back/forward and share work as expected.
      try {
        window.history.pushState({ hpsProduct: nextHandle }, '', u.pathname + u.search + u.hash);
      } catch (_) {}

      var activeTab = sectionEl.querySelector('[data-hps-content] .pfs__option--active[data-handle]');
      var currentHandle = activeTab ? activeTab.getAttribute('data-handle') : null;

      if (currentHandle !== nextHandle) {
        if (wantBuyScroll) {
          var onceSwap = function () {
            document.removeEventListener('jiyu:hps:product-changed', onceSwap);
            scrollToBuyBox(sectionEl);
          };
          document.addEventListener('jiyu:hps:product-changed', onceSwap);
        }
        loadProduct(sectionEl, nextHandle, products[nextHandle]);
      } else if (wantBuyScroll) {
        scrollToBuyBox(sectionEl);
      }
    });
  }

  // Find the most "buy-box-like" element inside the freshly-loaded PDP and
  // smooth-scroll to it, offset by ~80px so the target lands under (not
  // behind) the sticky header. Falls back to the section anchor if none of
  // those selectors hit (defensive — covers different PDP template variants).
  function scrollToBuyBox(sectionEl) {
    var content = sectionEl.querySelector('[data-hps-content]');
    if (!content) return;
    // One frame's delay so freshly-injected web components have laid out.
    requestAnimationFrame(function () {
      var target = content.querySelector(
        '[id^="vp-v2-variant-selects"], variant-selects, .product-form, product-form, [id^="ProductSubmitButton"]'
      );
      if (!target) target = sectionEl.querySelector('#hps-buy');
      if (!target) return;
      var rect = target.getBoundingClientRect();
      var offset = 80; // sticky-header allowance — matches .hps__anchor scroll-margin-top
      window.scrollTo({
        top: window.scrollY + rect.top - offset,
        behavior: 'smooth'
      });
    });
  }

  // target: igProduct()'s answer, the handle and variant to fetch. The cache
  // keys on target.handle, so an original and its copy never share an entry.
  function fetchProduct(target, info) {
    var handle = target.handle;
    if (cache[handle]) {
      // Cache hit — still poke the poster warmer in case the prefetch
      // landed before the warmer was wired up, or a prior visit cached
      // the HTML without warming. The Set guards against duplicates.
      warmVideoPosterCache(cache[handle]);
      return Promise.resolve(cache[handle]);
    }

    // When a `view` suffix is configured (info.view), we MUST go through
    // the full-page path with ?view=<suffix> so Shopify renders the
    // alternate template (e.g. product.toner-pads-switcher.json). The
    // section_id path can't compose with view — it always returns the
    // section instance whose ID matches, ignoring view — so a stale or
    // wrong section ID would render the OLD template. Skip section_id
    // entirely when view is set.
    if (info && info.view) {
      return fetchProductFullPage(target, info);
    }

    // Prefer the Section Rendering API (~170KB) over the full PDP fetch
    // (~550KB) when we know the section instance ID. Calling section_id
    // with the section's TEMPLATE INSTANCE ID (template--<id>__main, set
    // per product in the section settings) — not the section's file name
    // 'main-product' — makes Shopify render with the template's saved
    // settings (media_size, gallery_layout, etc.), not the schema defaults.
    // That's the difference that previously forced us to fetch the whole
    // page just to get correct wrapper classes. An Intelligems copy renders
    // its original's template, so the original's section ID fits it too.
    if (info && info.sectionId) {
      var sidUrl = productUrl(target, 'section_id=' + encodeURIComponent(info.sectionId));
      return fetch(sidUrl, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (sectionHtml) {
          // Section Rendering API returns the wrapped <section id="..."> already.
          if (!sectionHtml || sectionHtml.length < 200) {
            throw new Error('section response too small');
          }
          cache[handle] = sectionHtml;
          warmVideoPosterCache(sectionHtml);
          return sectionHtml;
        })
        .catch(function (err) {
          console.warn('[hps] section_id fetch failed, falling back to full page:', handle, err);
          return fetchProductFullPage(target, info);
        });
    }

    return fetchProductFullPage(target, info);
  }

  // Fallback for when no sectionId is configured for a handle, or when the
  // section_id fetch fails. Heavier but always works. Also the path used
  // when `info.view` is set — appends &view=<suffix> so Shopify renders
  // the alternate template (e.g. product.<view>.json).
  function fetchProductFullPage(target, info) {
    // productUrl() carries the same locale prefix as the section_id path.
    var handle = target.handle;
    var url = productUrl(target, info && info.view ? 'view=' + encodeURIComponent(info.view) : '');
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (fullHtml) {
        var doc = new DOMParser().parseFromString(fullHtml, 'text/html');
        var mainSection = doc.querySelector('section[id*="shopify-section-template"][id$="__main"]')
                       || doc.querySelector('div[id*="shopify-section-template"][id$="__main"]');
        if (!mainSection) {
          var pi = doc.querySelector('product-info');
          if (pi) {
            var s = pi;
            while (s && !(s.classList && s.classList.contains('shopify-section'))) s = s.parentElement;
            mainSection = s;
          }
        }
        if (!mainSection) throw new Error('main section not found in product page');
        var html = mainSection.outerHTML;
        cache[handle] = html;
        warmVideoPosterCache(html);
        return html;
      })
      .catch(function (err) {
        console.error('[hps] full-page fallback fetch failed:', handle, err);
        return null;
      });
  }

  // focusRequest ({ isCard, focusAtClick }) is set when a keyboard click
  // started the switch: focus then moves to the new product's entry once it is
  // mounted. Null for pointer clicks, the initial load and the "Shop Now" links.
  // afterWait: set when re-entered after waiting for Intelligems (see below).
  function loadProduct(sectionEl, handle, info, focusRequest, afterWait) {
    var content = sectionEl.querySelector('[data-hps-content]');
    if (!content) return;
    var focusApi = focusRequest ? window.JiYuSwitcherFocus : null;

    // Race-condition guard: rapid tab clicks can fire fetches in parallel and
    // resolve out of order. Only the most recent click's result should win.
    sectionEl._hpsLoadToken = (sectionEl._hpsLoadToken || 0) + 1;
    var myToken = sectionEl._hpsLoadToken;

    // Which product to show: this shopper's Intelligems copy, if any. Until
    // Intelligems is ready that can't be told, so start the original's
    // download (a shopper outside the test then loses no time) and decide once
    // it is, capped by IG_WAIT_MS. A newer click supersedes the wait.
    var target = igProduct(handle, info);
    if (!target.known && !afterWait) {
      sectionEl._hpsWaiting = true;
      fetchProduct(target, info);
      igReady().then(function () {
        if (sectionEl._hpsLoadToken !== myToken) return;
        sectionEl._hpsWaiting = false;
        loadProduct(sectionEl, handle, info, focusRequest, true);
      });
      return;
    }
    sectionEl._hpsWaiting = false;

    // Stash the currently-mounted product's DOM before we replace it, keyed
    // by the active tab's handle. Next time the customer comes back to this
    // handle we'll re-attach this exact node instead of rebuilding from HTML.
    // The active tab lives inside the rendered PDP fragment (the embedded
    // product-family-selector renders `<div class="pfs__option--active"
    // data-handle="<handle>">`), so we read it before the swap nukes content.
    var activeTab = content.querySelector('.pfs__option--active[data-handle]');
    var previousHandle = activeTab ? activeTab.getAttribute('data-handle') : null;
    var liveNode = content.firstElementChild;
    var isSwap = !!liveNode && !liveNode.classList.contains('hps__skeleton');
    // Only stash if it's a real rendered section (skip the skeleton fallback
    // shown during initial-load — it carries no real content to preserve).
    // Keyed by the handle that was fetched for it (an Intelligems copy's, or
    // the original's), which is what the lookup below uses.
    if (previousHandle && previousHandle !== handle && isSwap) {
      var shownKey = sectionEl._hpsShown && sectionEl._hpsShown.handle === previousHandle
        ? sectionEl._hpsShown.key
        : previousHandle;
      domCache[shownKey] = liveNode;
    }

    // FAST PATH: we've seen this product before. Re-attach the live DOM that
    // was already parsed, mounted, painted, and (for videos) had its posters
    // decoded into the browser's image cache. No fetch, no innerHTML rebuild,
    // no re-paint of the video block — switching is instant.
    if (domCache[target.handle]) {
      // Reserve current height so the page doesn't collapse during the brief
      // window between innerHTML='' and appendChild attaching the cached node.
      // The cached node was painted at this height before, but layout still
      // has to re-run when it re-enters the DOM. Cleared on next frame.
      var fastReservedHeight = content.offsetHeight;
      content.style.minHeight = fastReservedHeight + 'px';
      var fastRefocus = focusApi && focusApi.focusWithin(content, focusRequest.focusAtClick);
      content.innerHTML = '';
      content.appendChild(domCache[target.handle]);
      content.removeAttribute('aria-busy');
      sectionEl._hpsShown = { handle: handle, key: target.handle };

      // Re-run the family-selector's scrollActiveIntoView SYNCHRONOUSLY
      // before the browser paints the re-attached node. Detaching a node
      // (innerHTML='' on the parent) resets its descendants' scrollLeft to
      // 0, so without this the v3 Bundle & Save tab — which sits past the
      // visible viewport when 4 tabs are scrolled — would render at
      // scrollLeft=0 (Bundle & Save out of view) for one paint frame
      // before any later async retry catches it. Sync call here, run
      // BEFORE the next paint, means the customer never sees the
      // un-scrolled state. Belt-and-suspenders RAF retry mirrors PFS's
      // own retry chain for the cases where layout isn't fully settled
      // on the immediate read.
      var pfsEl = content.querySelector('[data-product-family-selector]');
      var pfsInst = pfsEl && pfsEl._pfsInstance;
      if (pfsInst && typeof pfsInst.scrollActiveIntoView === 'function') {
        try { pfsInst.scrollActiveIntoView(); } catch (e) {}
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            try { pfsInst.scrollActiveIntoView(); } catch (e) {}
          });
        });
      }
      if (fastRefocus) {
        focusApi.focusEntry(content.querySelector('[data-product-family-selector]'), handle, focusRequest.isCard);
      }

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          content.style.minHeight = '';
        });
      });
      // The embedded product-info gets re-upgraded via connectedCallback when
      // the node is re-attached. It re-reads data-update-url at variant-
      // change time, so the value we flipped to "false" on first mount sticks.
      // No need to re-activateInlineScripts: those scripts already ran when
      // the node was first inserted and modified the DOM accordingly. Re-
      // running would do things like double-bind event listeners.
      try {
        document.dispatchEvent(new CustomEvent('jiyu:hps:product-changed', {
          detail: { handle: handle }
        }));
      } catch (e) {}
      // Re-warm the variant section + image caches for this newly-active
      // product. The first mount fired this on idle; the cache should
      // already be hot, but a re-trigger is essentially free (both scripts
      // skip already-cached entries) and protects against the case where
      // the first mount's idle slot was skipped under heavy main-thread load.
      warmVariantCachesForCurrentProduct();
      return;
    }

    fetchProduct(target, info).then(function (html) {
      if (sectionEl._hpsLoadToken !== myToken) return; // a newer click superseded us
      if (!html) {
        content.innerHTML = '<p class="hps__error">Couldn\'t load product. Please refresh the page.</p>';
        content.removeAttribute('aria-busy');
        return;
      }

      // Shopify returns: <div id="shopify-section-<id>" class="shopify-section ..."> ... </div>
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var newSection = tmp.firstElementChild;
      if (!newSection) {
        content.innerHTML = '<p class="hps__error">Empty response from Shopify.</p>';
        content.removeAttribute('aria-busy');
        return;
      }
      // Only a swap replaces posters the customer may be looking at. The first
      // load replaces the skeleton, far down the homepage while it is still
      // loading, so its posters stay lazy.
      if (isSwap) eagerVideoPosters(newSection);

      // Same height reservation as the domCache fast path — see comment above.
      var fetchReservedHeight = content.offsetHeight;
      content.style.minHeight = fetchReservedHeight + 'px';
      // Checked at the swap, not the click: a customer who moved on while the
      // product downloaded keeps their place.
      var fetchRefocus = focusApi && focusApi.focusWithin(content, focusRequest.focusAtClick);
      content.innerHTML = '';
      content.appendChild(newSection);
      content.removeAttribute('aria-busy');
      sectionEl._hpsShown = { handle: handle, key: target.handle };
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          content.style.minHeight = '';
        });
      });

      // The PDP's <product-info data-update-url="true"> rewrites the address
      // bar to /products/<handle>?variant=<id> on every variant change via
      // window.history.replaceState. On the homepage that would clobber the
      // shareable ?product=<handle> URL that updateUrlParam() maintains and
      // change the path away from "/". Flip the flag so its updateURL() early-
      // returns. The share-button URL update still runs (it precedes the
      // early-return), so PDP share links remain correct.
      var embeddedProductInfo = newSection.querySelector('product-info');
      if (embeddedProductInfo) embeddedProductInfo.dataset.updateUrl = 'false';

      // Shopify's Section Rendering API returns inert <script> nodes. Re-create
      // them so they execute in the correct order.
      activateInlineScripts(newSection);

      // Trigger Shopify's section reload event so any web component or app
      // (variant-selects, product-form, Appstle subscription widget, etc.)
      // re-initializes for the freshly-injected DOM.
      try {
        newSection.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: newSection.id.replace('shopify-section-', '') }
        }));
      } catch (e) {}
      // After section:load, which constructs the embedded family selector.
      if (fetchRefocus) {
        focusApi.focusEntry(newSection.querySelector('[data-product-family-selector]'), handle, focusRequest.isCard);
      }

      // Re-init Shopify dynamic checkout button, Judge.me preview badge.
      if (window.Shopify && window.Shopify.PaymentButton) {
        try { window.Shopify.PaymentButton.init(); } catch (e) {}
      }
      // Judge.me's loader.js sets up its widgets at PDP page-load by
      // removing the inline display:none on .jdgm-prev-badge and adding
      // jdgm--done-setup to wrappers (which a Judge.me CSS rule keys off
      // of to flip visibility). The loader doesn't re-run on AJAX-injected
      // DOM, so we replicate just enough of it: strip the inline display
      // and tag the wrapper. The matching color/visibility CSS rules live
      // in homepage-product-switcher.css.
      newSection.querySelectorAll('.jdgm-prev-badge[style*="display:none"], .jdgm-prev-badge[style*="display: none"]').forEach(function (el) {
        el.style.removeProperty('display');
      });
      newSection.querySelectorAll('.jdgm-widget').forEach(function (el) {
        el.classList.add('jdgm--done-setup');
      });

      // Broadcast so sibling sections (FAQ, reviews) can sync their active
      // tab to the product the customer is currently looking at. Listeners
      // match on detail.handle. Fired on initial mount AND on each click.
      try {
        document.dispatchEvent(new CustomEvent('jiyu:hps:product-changed', {
          detail: { handle: handle }
        }));
      } catch (e) {}

      // Warm the variant section HTML + gallery image caches for the
      // freshly-injected PDP. Both preload scripts auto-ran on window.load
      // earlier — but the homepage skeleton was up then, so they found
      // nothing. This re-triggers them against the now-mounted product-info
      // + media-gallery. Both functions schedule via requestIdleCallback
      // internally; calling them during this sync block is fine.
      warmVariantCachesForCurrentProduct();
    });
  }

  // Re-run the same idle-deferred scans that product-variant-section-preload.js
  // and product-variant-image-preload.js auto-run on PDPs at window.load.
  // The exposed callables are no-ops if the scripts aren't loaded (defensive
  // against future template changes that strip them).
  function warmVariantCachesForCurrentProduct() {
    if (window.__variantSectionPreloadRun) {
      try { window.__variantSectionPreloadRun(); } catch (e) {}
    }
    if (window.__variantImagePreloadRun) {
      try { window.__variantImagePreloadRun(); } catch (e) {}
    }
  }

  function activateInlineScripts(container) {
    container.querySelectorAll('script').forEach(function (oldScript) {
      if (oldScript.src) return; // external scripts re-fetch automatically
      if (oldScript.type && oldScript.type !== 'text/javascript' && oldScript.type !== '') return;
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function updateUrlParam(handle) {
    try {
      var url = new URL(window.location);
      url.searchParams.set('product', handle);
      window.history.replaceState({ hpsProduct: handle }, '', url);
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    // Re-init if a switcher section is itself reloaded (customizer edits).
    var inner = e.target.querySelector ? e.target.querySelector('[data-homepage-product-switcher]') : null;
    if (inner) init();
  });
})();
